import { useCallback } from 'react'

import { useEditor } from '@craftjs/core'
import { getRandomId } from '@craftjs/utils'
import { mapValues } from 'lodash'

const createMapping = () => {
  const ids = {}

  return id => {
    if (!ids[id]) {
      ids[id] = getRandomId()
    }
    return ids[id]
  }
}

const remapOptions = (options, map) => {
  if (typeof options === 'object' && !Array.isArray(options)) {
    return mapValues(options, option => ({
      ...option,
      id: map(option.id),
    }))
  }

  return options
}

export function serializeNodeTree(getNode, nodeId) {
  const map = createMapping()
  const { nodes, rootNodeId } = getNode(nodeId).toNodeTree()

  return Object.values(nodes).reduce((newNodes, node) => {
    const { nodes: childrenNodes, linkedNodes, parent, custom, ...data } = getNode(node.id).toSerializedNode()
    const newId = map(node.id)

    newNodes[newId] = {
      ...data,
      id: newId,
      parent: node.id === rootNodeId ? null : map(parent),
      nodes: childrenNodes && childrenNodes.map(id => map(id)),
      linkedNodes: linkedNodes && mapValues(linkedNodes, id => map(id)),
      custom: {
        ...custom,
        options: remapOptions(custom.options, map),
      },
    }

    return newNodes
  }, {})
}

export function parseSerializedTree(parseSerializedNode, data) {
  const map = createMapping()
  let rootNodeId = null

  const nodes = Object.values(data).reduce((newNodes, node) => {
    const { nodes: childrenNodes, linkedNodes, parent, custom, ...rest } = node
    const newId = map(node.id)

    if (parent === null) {
      rootNodeId = newId
    }

    const newSerialized = {
      ...rest,
      parent: parent ? map(parent) : null,
      nodes: childrenNodes && childrenNodes.map(id => map(id)),
      linkedNodes: linkedNodes && mapValues(linkedNodes, id => map(id)),
      custom: {
        ...custom,
        options: remapOptions(custom.options, map),
      },
    }

    newNodes[newId] = parseSerializedNode(newSerialized).toNode(newNode => {
      newNode.id = newId
    })

    return newNodes
  }, {})

  return { nodes, rootNodeId }
}

export function cloneNodeTree(getNode, parseSerializedNode, nodeId) {
  const map = createMapping()
  const { nodes, rootNodeId } = getNode(nodeId).toNodeTree()

  const cloned = Object.values(nodes).reduce((newNodes, node) => {
    const { nodes: childrenNodes, linkedNodes, parent, ...data } = getNode(node.id).toSerializedNode()
    const newId = map(node.id)

    const newSerialized = {
      ...data,
      parent: node.id === rootNodeId ? null : map(parent),
      nodes: childrenNodes && childrenNodes.map(id => map(id)),
      linkedNodes: linkedNodes && mapValues(linkedNodes, id => map(id)),
    }

    newNodes[newId] = parseSerializedNode(newSerialized).toNode(newNode => {
      newNode.id = newId
    })

    return newNodes
  }, {})

  return { nodes: cloned, rootNodeId: map(rootNodeId) }
}

export function useSerialize(nodeId) {
  const { query: { node: getNode } } = useEditor()

  return useCallback(() => {
    if (!nodeId) {
      return null
    }

    try {
      return serializeNodeTree(getNode, nodeId)
    } catch (error) {
      console.error('[serialize-block] Failed to serialize block', {
        nodeId,
        error,
        message: error?.message,
        stack: error?.stack,
      })

      throw error
    }
  }, [nodeId, getNode])
}

export function useSerializeWithoutRemapping(nodeId) {
  const { query: { node: getNode } } = useEditor()

  return useCallback(() => {
    if (!nodeId) {
      return null
    }

    const { nodes, rootNodeId } = getNode(nodeId).toNodeTree()

    const nodesReducer = (newNodes, node) => {
      const serialized = getNode(node.id).toSerializedNode()

      newNodes[node.id] = {
        ...serialized,
        id: node.id,
        parent: node.id === rootNodeId ? null : serialized.parent,
      }

      return newNodes
    }

    return Object.values(nodes).reduce(nodesReducer, {})
  }, [nodeId, getNode])
}

export function useParseSerialized() {
  const { query: { parseSerializedNode } } = useEditor()

  return useCallback(data => parseSerializedTree(parseSerializedNode, data), [parseSerializedNode])
}
